import { FiX, FiAlertTriangle } from 'react-icons/fi';

/**
 * Reusable Upgrade Modal Component
 * Displays upgrade prompts for advanced features
 * 
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {Function} onClose - Callback to close the modal
 * @param {string} reason - Type of upgrade prompt: 'multi-service' | 'class-capacity'
 * @param {string} featureName - Optional feature name for customization
 */
const UpgradeModal = ({ isOpen, onClose, reason = 'class-capacity' }) => {
  if (!isOpen) return null;

  const getModalContent = () => {
    switch (reason) {
      case 'multi-service':
        return {
          title: 'Upgrade to Pro',
          message: 'Free account limited to one service. Upgrade to offer multiple services.',
          benefits: [
            'Offer multiple services simultaneously',
            'Up to 50 students per class'
          ]
        };
      case 'class-capacity':
      default:
        return {
          title: 'Upgrade to Pro',
          message: 'Free account limited to 10 students per class. Upgrade to enroll up to 50 students per class.',
          benefits: [
            'Up to 50 students per class',
            'Multiple services support'
          ]
        };
    }
  };

  const content = getModalContent();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-sm border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">{content.title}</h3>
          <button 
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800"
            aria-label="Close modal"
          >
            <FiX size={18} />
          </button>
        </div>
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-red-50 p-4 rounded-lg">
            <FiAlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
            <p className="text-sm text-red-700">{content.message}</p>
          </div>
          <ul className="space-y-2">
            {content.benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-2">
                <svg className="h-5 w-5 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-gray-600">{benefit}</span>
              </li>
            ))}
          </ul>
          <div className="flex gap-3 mt-6">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 transition-colors"
            >
              Continue Free
            </button>
            <button
              onClick={() => window.location.href = '/pricing'}
              className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
